import { useEffect, useState } from 'react';
import { Anchor } from 'lucide-react';
import type { StorefrontBrand } from '@/context/StorefrontContext';

type TenantBrandMarkSize = 'sm' | 'md' | 'lg';

interface TenantBrandMarkProps {
  brand: StorefrontBrand;
  size?: TenantBrandMarkSize;
  subtitle?: string;
  detail?: string;
  stacked?: boolean;
  tone?: 'default' | 'inverse';
}

const SIZE_MAP: Record<TenantBrandMarkSize, { box: string; icon: string; title: string; subtitle: string }> = {
  sm: {
    box: 'w-8 h-8 rounded-lg',
    icon: 'w-4 h-4',
    title: 'text-sm',
    subtitle: 'text-[10px]',
  },
  md: {
    box: 'w-10 h-10 rounded-xl',
    icon: 'w-5 h-5',
    title: 'text-base',
    subtitle: 'text-[11px]',
  },
  lg: {
    box: 'w-14 h-14 rounded-2xl',
    icon: 'w-7 h-7',
    title: 'text-xl',
    subtitle: 'text-xs',
  },
};

export function TenantBrandMark({
  brand,
  size = 'md',
  subtitle,
  detail,
  stacked = false,
  tone = 'default',
}: TenantBrandMarkProps) {
  const [showFallback, setShowFallback] = useState(!brand.logoUrl);
  const sizeClasses = SIZE_MAP[size];

  useEffect(() => {
    setShowFallback(!brand.logoUrl);
  }, [brand.logoUrl]);

  const media = showFallback ? (
    <div className={`${sizeClasses.box} bg-gradient-to-br from-brand-700 to-ocean-500 flex items-center justify-center shadow-sm`}>
      <Anchor className={`${sizeClasses.icon} text-white`} />
    </div>
  ) : (
    <img
      src={brand.logoUrl}
      alt={`${brand.brandShortName} logo`}
      className={`${sizeClasses.box} object-cover bg-white border border-slate-200 shadow-sm`}
      onError={() => setShowFallback(true)}
    />
  );

  const titleColor = tone === 'inverse' ? 'text-white' : 'text-slate-900';
  const subtitleColor = tone === 'inverse' ? 'text-slate-300' : 'text-slate-500';
  const detailColor = tone === 'inverse' ? 'text-slate-400' : 'text-slate-400';
  const textBlock = (
    <div className="min-w-0">
      <span className={`font-semibold leading-tight block truncate ${titleColor} ${sizeClasses.title}`}>
        {brand.brandShortName}
      </span>
      {(subtitle || detail) && (
        <span className={`block leading-tight truncate ${subtitleColor} ${sizeClasses.subtitle}`}>
          {subtitle ?? detail}
        </span>
      )}
      {subtitle && detail && (
        <span className={`block text-[10px] leading-tight truncate ${detailColor}`}>
          {detail}
        </span>
      )}
    </div>
  );

  if (stacked) {
    return (
      <div className="flex flex-col items-center text-center gap-3">
        {media}
        {textBlock}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 min-w-0">
      {media}
      {textBlock}
    </div>
  );
}
